"use client";

import * as React from "react";

import { cn } from "../lib/utils";
import { Icon, type IconProps } from "./icon";

/**
 * Empty — custom Screenrights empty-state primitive.
 *
 * Hand-authored and restyled for our design system; adapted from shadcn's
 * compositional Empty (https://ui.shadcn.com/docs/components/empty). This is a
 * CUSTOM component — it is NOT produced by the shadcn CLI and is neither the
 * stock shadcn v3 nor v4 component. Compose the parts and customise per empty
 * state in the frontend as needed:
 *
 *   <Empty>
 *     <EmptyHeader>
 *       <EmptyMedia>
 *         <EmptyIcon icon="folderOpen" />
 *       </EmptyMedia>
 *       <EmptyTitle>No titles yet</EmptyTitle>
 *       <EmptyDescription>Titles you register will appear here.</EmptyDescription>
 *     </EmptyHeader>
 *     <EmptyContent>
 *       <Button>Add a title</Button>
 *     </EmptyContent>
 *   </Empty>
 */
const Empty = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn(
      "flex min-w-0 flex-1 flex-col items-center justify-center gap-4 text-balance rounded-lg border border-border bg-card px-6 py-6 text-center",
      className,
    )}
    data-slot="empty"
    ref={ref}
    {...props}
  />
));
Empty.displayName = "Empty";

const EmptyHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn("flex flex-col items-center gap-2.5 text-center", className)}
    data-slot="empty-header"
    ref={ref}
    {...props}
  />
));
EmptyHeader.displayName = "EmptyHeader";

const EmptyMedia = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn("flex items-center justify-center", className)}
    data-slot="empty-media"
    ref={ref}
    {...props}
  />
));
EmptyMedia.displayName = "EmptyMedia";

type EmptyIconProps = Omit<IconProps, "icon" | "size"> & {
  size?: IconProps["size"];
  icon?: IconProps["icon"];
};

const EmptyIcon = ({
  className,
  size = "2xl",
  icon = "info",
  ...props
}: EmptyIconProps) => (
  <Icon
    className={cn("mb-1 text-muted-foreground", className)}
    icon={icon}
    size={size}
    {...props}
  />
);
EmptyIcon.displayName = "EmptyIcon";

const EmptyTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn("text-base font-medium leading-none", className)}
    data-slot="empty-title"
    ref={ref}
    {...props}
  />
));
EmptyTitle.displayName = "EmptyTitle";

const EmptyDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    className={cn(
      "text-sm/relaxed text-muted-foreground [&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4",
      className,
    )}
    data-slot="empty-description"
    ref={ref}
    {...props}
  />
));
EmptyDescription.displayName = "EmptyDescription";

const EmptyContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn(
      "flex w-full min-w-0 max-w-sm flex-col items-center gap-4 text-balance text-sm",
      className,
    )}
    data-slot="empty-content"
    ref={ref}
    {...props}
  />
));
EmptyContent.displayName = "EmptyContent";

type EmptyProps = React.ComponentProps<typeof Empty>;
type EmptyHeaderProps = React.ComponentProps<typeof EmptyHeader>;
type EmptyTitleProps = React.ComponentProps<typeof EmptyTitle>;
type EmptyDescriptionProps = React.ComponentProps<typeof EmptyDescription>;
type EmptyContentProps = React.ComponentProps<typeof EmptyContent>;

export {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyIcon,
  EmptyMedia,
  EmptyTitle,
  type EmptyContentProps,
  type EmptyDescriptionProps,
  type EmptyHeaderProps,
  type EmptyIconProps,
  type EmptyProps,
  type EmptyTitleProps,
};
